/**
 * Plan a follow-up for an interaction — the client half of Step 3's deferral.
 *
 * Step 3's «یادداشت پیگیری» rode in the interaction's description tail, labelled
 * as a placeholder, because there was nowhere else to put it: the real
 * follow-up is an activity with a date, a reminder and a calendar, and that
 * object did not exist yet. It does now, and this is what creates it.
 *
 * It goes through `createFollowUpFromInteraction` rather than a plain
 * `createActivity`, because two things must happen together: the activity is
 * created in the scheduling service AND the interaction in the CRM is pointed
 * at it. That is a saga, not a transaction — the BFF compensates by cancelling
 * the activity when the link fails, and says so when even that fails.
 */
import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import {
  FormDialog,
  JalaliDateField,
  MutationError,
  TextField,
} from '../FormKit.web.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';
import { isoDateAndTimeToInstant, isoToDateAndTime } from '../../lib/format.js';
import {
  ACTIVITY_TYPES_QUERY,
  CREATE_ACTIVITY_TYPE,
  CREATE_FOLLOW_UP_FROM_INTERACTION,
} from '../../lib/queries.js';
import { nextWorkingDay } from '../../screens/FollowUps.web.jsx';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';

export function FollowUpDialog({ interaction, contactId, onOpenChange, onCreated }) {
  const open = Boolean(interaction);
  const { isAdmin } = useSession();
  const [form, setForm] = useState({ typeId: '', title: '', date: '', time: '09:00' });

  const { data: typeData, loading: typesLoading } = useQuery(ACTIVITY_TYPES_QUERY, {
    skip: !open,
  });
  const types = typeData?.activityTypes ?? [];

  const [createFollowUp, state] = useMutation(CREATE_FOLLOW_UP_FROM_INTERACTION);
  // The same dead end as the calendar's: an empty vocabulary would make
  // planning a follow-up impossible from here too.
  const [createType, createTypeState] = useMutation(CREATE_ACTIVITY_TYPE, {
    refetchQueries: ['ActivityTypes'],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (!open) return;
    // Prefilled for the next working day, like the inbox's Accept: the value of
    // a one-click follow-up is entirely in not having to fill a form.
    const when = isoToDateAndTime(nextWorkingDay().toISOString());
    setForm({
      typeId: '',
      title: interaction?.type?.labelFa
        ? `${S.activity.createdFromInteraction}: ${interaction.type.labelFa}`
        : S.activity.createdFromInteraction,
      date: when.date,
      time: when.time,
    });
  }, [open, interaction]);

  useEffect(() => {
    if (!form.typeId && types.length > 0) {
      setForm((current) => ({ ...current, typeId: types[0].id }));
    }
  }, [types, form.typeId]);

  async function submit() {
    const startsAt = isoDateAndTimeToInstant(form.date, form.time);
    if (!startsAt) return;
    await createFollowUp({
      variables: {
        interactionId: interaction.id,
        input: {
          typeId: form.typeId,
          title: form.title,
          startsAt,
          contactId,
        },
      },
    });
    onCreated?.();
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.activity.createdFromInteraction}
      submitLabel={S.activity.create}
      busy={state.loading}
      onSubmit={submit}
    >
      <MutationError error={state.error} />

      <VocabularyField
        label={S.activity.type}
        value={form.typeId}
        onChange={(id) => setForm((c) => ({ ...c, typeId: id }))}
        options={types}
        loading={typesLoading}
        required
        canCreate={isAdmin}
        codePrefix="ACTIVITY"
        creating={createTypeState.loading}
        createError={createTypeState.error}
        onCreate={async (input) => {
          const created = await createType({ variables: { input } });
          return created.data?.createActivityType;
        }}
      />

      <TextField
        label={S.activity.subject}
        value={form.title}
        onChange={(event) => setForm((c) => ({ ...c, title: event.target.value }))}
        required
      />

      <div className="flex gap-3">
        <JalaliDateField
          label={S.activity.date}
          value={form.date}
          onChange={(value) => setForm((c) => ({ ...c, date: value }))}
        />
        <TextField
          label={S.activity.startTime}
          value={form.time}
          onChange={(event) => setForm((c) => ({ ...c, time: event.target.value }))}
          dir="ltr"
        />
      </div>
    </FormDialog>
  );
}
